import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import Banner from "@dtci/spellbook/Banner";
import Button from "@dtci/spellbook/Button";
import ContentBlock from "@dtci/spellbook/ContentBlock";
import DataGrid from "@dtci/spellbook/DataGrid";
import Typography from "@dtci/spellbook/Typography";
import Moment from "moment-timezone";
import { useConfirmationDialog } from "tap/components/dialogs/dialogs/confirmation.jsx";
import { EditRateCardDrawer } from "../drawers/rate-card-drawer";
import { ViewRateCardDrawer } from "../drawers/view";
import { useAgGridApi } from "tap/components/AGGrid/hooks.js";
import { useRateCardGridUtils } from "./utils/gridUtils";
import { useRateCardsData } from "../../hooks/useRateCardsData";
import { useLoading } from "tap/utils/hooks/useLoading.js";
import { useToasts } from "tap/utils/hooks/useToasts.js";
import { usePermissions } from "tap/utils/permissions.js";
import { tapApi } from "tap/api.js";
import {
  exportRateCardsToExcel,
  fetchFullRateCardsForExport,
  transformRateCardsForExport,
} from "../../utils/rateCardExport";
import { normalizeRateCardTierPricesForForm } from "../drawers/rate-card-drawer/utils/validation";
import { usePaginatedGridSelection } from "tap/utils/hooks/usePaginatedGridSelection.js";
import { getAllItemsRange } from "tap/utils/pagination.js";
import gridStyles from "../../../../components/AGGrid/details-table.module.scss";
import "./styles.scss";

export const RateCardsGrid = () => {
  const agGridApi = useAgGridApi();
  const { openConfirmationDialog } = useConfirmationDialog();
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const { showErrorToast, showSuccessToast } = useToasts();
  const { isTapAdmin } = usePermissions();

  const { rateCards, refreshRateCards, totalItems } = useSelector((s) => s.rateCards);
  const { fetchRateCards, updateRateCards, rateCardsRange, filterForm } =
    useRateCardsData();

  useEffect(() => {
    if (refreshRateCards) {
      refreshRateCardsList();
      updateRateCards({ refreshRateCards: false });
    }
  }, [refreshRateCards]);

  const fetchAllRows = useCallback(async () => {
    if (!totalItems) {
      return [];
    }
    const data = await fetchRateCards(getAllItemsRange(totalItems), filterForm);
    return data?.content ?? [];
  }, [fetchRateCards, filterForm, totalItems]);

  const {
    bannerText,
    onSelectionChanged,
    onRowSelected,
    clearSelection,
    resolveSelectedRows,
  } = usePaginatedGridSelection({
    totalItems,
    fetchAllRows,
    entityName: "item",
    rowData: rateCards ?? [],
    gridApiRef: agGridApi.AgGridApiRef,
    resetKey: `${totalItems}-${rateCardsRange}-${JSON.stringify(filterForm)}`,
  });

  const onOpenViewRateCardDrawer = useCallback(
    async (rateCard) => {
      if (!rateCard || !rateCard.id) return;
      setLoadingTrue();
      try {
        const [data, historyData] = await Promise.all([
          tapApi.rateCardById(rateCard.id),
          tapApi.rateCardHistory(rateCard.id),
        ]);

        if (data?.code || data?.httpCode || data?.error) {
          showErrorToast(data?.message || "Failed to load rate card details.");
          return;
        }

        const mappedHistory = Array.isArray(historyData)
          ? historyData.map((h) => ({
              label: `${Moment(Moment.utc(h.historyCreatedAt).toDate())
                .tz(Moment.tz.guess())
                .format(
                  "MM/DD/YYYY, h:mm:ss A z",
                )} | ${h.createdBy?.username ?? ""}`,
              value: h.id,
              data: h,
            }))
          : [];

        updateRateCards({
          isViewRateCardDrawerOpen: true,
          selectedRateCard: data,
          rateCardHistory: mappedHistory,
        });
      } catch (error) {
        showErrorToast("Failed to load rate card details.");
        console.error("Error loading rate card details:", error);
      } finally {
        setLoadingFalse();
      }
    },
    [setLoadingTrue, setLoadingFalse, showErrorToast, updateRateCards],
  );

  const onOpenEditRateCardModal = useCallback(
    async (data) => {
      if (!data || !data.id) return;

      setLoadingTrue();
      try {
        const [currency, format, windowSequence, rateCard] = await Promise.all([
          tapApi.currency(),
          tapApi.format(),
          tapApi.windowSequence(),
          tapApi.rateCardById(data?.id),
        ]);

        if (rateCard?.code || rateCard?.httpCode || rateCard?.error) {
          showErrorToast(rateCard?.message || "Failed to load rate card.");
          return;
        }

        // Sort tierAliases by sortOrder (matches old behavior)
        if (rateCard?.tierAliases) {
          rateCard.tierAliases.sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
          );
        }

        const rateCardForForm = normalizeRateCardTierPricesForForm(rateCard);

        updateRateCards({
          currencyOptions: currency || [],
          formatOptions: format || [],
          windowSequenceOptions: windowSequence || [],
          isEditDrawerOpen: true,
          selectedRateCard: rateCardForForm,
        });
      } catch (error) {
        showErrorToast("Failed to load rate card.");
        console.error("Error loading rate card:", error);
      } finally {
        setLoadingFalse();
      }
    },
    [setLoadingTrue, setLoadingFalse, showErrorToast, updateRateCards],
  );

  const { columns } = useRateCardGridUtils({
    columnHandlers: [onOpenViewRateCardDrawer, onOpenEditRateCardModal],
    isTapAdmin,
  });

  const handleClose = () => {
    clearSelection();
  };

  const refreshRateCardsList = async () => {
    const data = await fetchRateCards(rateCardsRange, filterForm);
    if (data) {
      updateRateCards({
        rateCards: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber,
      });
    }
  };

  const handleSave = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    const isSingle = selectedRows.length === 1;
    openConfirmationDialog(
      isSingle ? "Save as TPR Rate Card" : "Save as TPR Rate Cards",
      "Confirm",
      [
        isSingle
          ? "Are you sure you want to save as TPR this rate card?"
          : "Are you sure you want to save as TPR the selected rate cards?",
      ],
      async () => {
        setLoadingTrue();
        try {
          const ids = selectedRows.map((row) => row.id);
          const result = await tapApi.saveAsTPR(ids);
          if (result?.code || result?.httpCode || result?.error) {
            showErrorToast(
              result?.message ||
                result?.error?.message ||
                "Rate Card(s) could not save as TPR rate cards",
            );
          } else {
            await refreshRateCardsList();
            showSuccessToast("Success, Rate Card(s) Saved as TPR Rate Card(s)");
          }
        } catch (error) {
          showErrorToast("Error saving Rate Card(s).");
          console.error("Error saving rate card(s):", error);
        } finally {
          handleClose();
          setLoadingFalse();
        }
      },
    );
  };

  const handleCopy = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    const isSingle = selectedRows.length === 1;
    openConfirmationDialog(
      isSingle ? "Copy Rate Card" : "Copy Rate Cards",
      "Confirm",
      [
        isSingle
          ? "Are you sure you want to copy this rate card?"
          : "Are you sure you want to copy the selected rate cards?",
      ],
      async () => {
        setLoadingTrue();
        try {
          const ids = selectedRows.map((row) => row.id);
          const result = await tapApi.copyRateCard(ids);
          if (result?.code || result?.httpCode || result?.error) {
            showErrorToast(
              result?.message ||
                result?.error?.message ||
                "Error: rate card name(s) already exists",
            );
          } else {
            await refreshRateCardsList();
            showSuccessToast("Rate Card(s) Copied");
          }
        } catch (error) {
          showErrorToast("Error copying rate cards.");
          console.error("Error copying rate card(s):", error);
        } finally {
          handleClose();
          setLoadingFalse();
        }
      },
    );
  };

  const handleInactivate = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    const isSingle = selectedRows.length === 1;
    openConfirmationDialog(
      isSingle ? "Inactivate Rate Card" : "Inactivate Rate Cards",
      "Confirm",
      [
        isSingle
          ? "Are you sure you want to inactivate this rate card?"
          : "Are you sure you want to inactivate the selected rate cards?",
      ],
      async () => {
        setLoadingTrue();
        try {
          const ids = selectedRows.map((row) => row.id);
          const result = await tapApi.inactivateRateCard(ids);
          if (result?.code || result?.httpCode || result?.error) {
            showErrorToast(
              result?.message ||
                result?.error?.message ||
                "Rate Card(s) could not inactivate",
            );
          } else {
            await refreshRateCardsList();
            showSuccessToast("Success, Rate Card(s) Inactivated");
          }
        } catch (error) {
          showErrorToast("Error inactivating rate card(s).");
          console.error("Error inactivating rate card(s):", error);
        } finally {
          handleClose();
          setLoadingFalse();
        }
      },
    );
  };

  const handleExport = async () => {
    const selectedRows = await resolveSelectedRows();
    if (selectedRows.length === 0) {
      return;
    }
    setLoadingTrue();
    try {
      const ids = selectedRows.map((row) => row.id);
      const rateCards = await fetchFullRateCardsForExport(
        ids,
        tapApi.rateCardById,
      );

      if (rateCards.length === 0) {
        showErrorToast("Failed to export rate card data.");
        return;
      }

      const flatRows = transformRateCardsForExport(rateCards);
      await exportRateCardsToExcel(flatRows, "ratecards");
    } catch (error) {
      showErrorToast("Error exporting rate cards.");
      console.error("Error exporting rate card(s):", error);
    } finally {
      setLoadingFalse();
    }
  };

  return (
    <>
      {bannerText && (
        <Banner
          faPackage="far"
          iconTypePrimary={"fa-check-circle"}
          role="status"
          type={"success"}
          onClose={handleClose}
          className="rate-cards-selection-banner"
        >
          <ContentBlock className="rate-cards-banner-content">
            <Typography className="main-banner-text" variant="T20">
              {bannerText}
            </Typography>
            <div className="rate-cards-banner-actions">
              {isTapAdmin && (
                <Button
                  className="rate-cards-banner-button"
                  iconType="fa-solid fa-floppy-disk"
                  type="button"
                  styleType="link"
                  onClick={handleSave}
                >
                  SAVE AS TPR
                </Button>
              )}
              {isTapAdmin && (
                <Button
                  className="rate-cards-banner-button"
                  iconType="fa-solid fa-copy"
                  type="button"
                  styleType="link"
                  onClick={handleCopy}
                >
                  COPY
                </Button>
              )}
              {isTapAdmin && (
                <Button
                  className="rate-cards-banner-button-danger"
                  iconType="fa-solid fa-trash"
                  type="button"
                  styleType="link"
                  onClick={handleInactivate}
                >
                  INACTIVATE
                </Button>
              )}
              <Button
                className="rate-cards-banner-button"
                iconType="fa-solid fa-arrow-down-to-line"
                type="button"
                styleType="link"
                onClick={handleExport}
              >
                EXPORT
              </Button>
            </div>
          </ContentBlock>
        </Banner>
      )}
      <DataGrid
        className={gridStyles.dataGrid}
        autoSizing
        agGrid={{
          ref: agGridApi.AgGridApiRef,
          rowData: rateCards ?? [],
          suppressColumnStateReset: true,
          headerHeight: 45,
          onSelectionChanged,
          onRowSelected,
          defaultColDef: {
            wrapText: true,
            autoHeight: true,
            resizable: true,
            suppressHeaderMenuButton: true,
            cellStyle: {
              paddingTop: "10px",
              paddingBottom: "10px",
              wordBreak: "break-word",
            },
          },
          getRowId: (params) => String(params.data.id),
          suppressScrollOnNewData: true,
        }}
        bordered="none"
        columnDefs={columns}
        sortable={false}
        striped
        selectable
        noResultsMessage={"No Rate Cards Found"}
      />
      <EditRateCardDrawer />
      <ViewRateCardDrawer />
    </>
  );
};
